// src/hooks/useFlowFormFactorConfig.js
import { useState } from 'react';

import { useExperimentFlagExposed } from '@zenbusiness/application-commons-experiments';
import useAsyncEffect from '@zenbusiness/application-commons-hooks/useAsyncEffect';

import { FORM_FACTORS } from '#client/constants';
import { ENABLE_FORM_FACTOR_FOR_MOBILE_FLOWS } from '#client/constants/experimentFlags';

/**
 * Builds a flow config filename based on the formFactor.
 *
 * @param {string} flowId - The ID of the flow
 * @param {string} formFactor - The form factor (phone, tablet, desktop)
 * @returns {string} The flow config filename to import
 */
function buildFormFactorConfigFilename(flowId, formFactor) {
  const hasValidFormFactor = Object.values(FORM_FACTORS).includes(formFactor);
  return hasValidFormFactor ? `${flowId}.${formFactor}` : `${flowId}`;
}

/**
 * Hook to load the appropriate flow config based on form factor.
 *
 * @param {object} defaultConfig - The default flow configuration
 * @returns {object} The active flow configuration and loading state
 */
function useFlowFormFactorConfig(defaultConfig) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get form factor value from experiment flag variation value.
  const { value: formFactor } = useExperimentFlagExposed(
    ENABLE_FORM_FACTOR_FOR_MOBILE_FLOWS,
    'control'
  );

  // For future implementation:
  // const { formFactor } = useWindowConfig();

  // Load the appropriate flow config based on form factor.
  useAsyncEffect(async () => {
    try {
      const formFactorConfigFilename = buildFormFactorConfigFilename(defaultConfig.id, formFactor);
      const { default: formFactorConfig } = await import(
        `#client/config/flows/${formFactorConfigFilename}`
      );
      setConfig(formFactorConfig);
    } catch (error) {
      setConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  }, [defaultConfig, formFactor]);

  return { config, loading };
}

export default useFlowFormFactorConfig;
